import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { SelektionskriterienDetailEntryMeta } from '../../../../../core/batch-auftrag-api';
import { ControlValueAccessor, FormsModule, NgForm } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { CapAlertService } from '../../../../../core/extension/exceptions/cap-alert.service';
import { CapAlertLevel } from '../../../../../core/extension/exceptions/cap-alert-level.enum';
import { CapiWizardComponent } from '../../../shared/capi-wizard/capi-wizard.component';
import { CapFormColumnsDirective } from '../../../../../shared/form-control/form-columns.directive';
import { AuftragExportSelectionComponent } from '../../../shared/widgets/auftrag-export-selection/auftrag-export-selection.component';
import { MetaDisablerDirective } from '../../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../../shared/directives/meta-validator.directive';
import { AuftragPersonartSelectionComponent } from '../../../shared/widgets/auftrag-personart-selection/auftrag-personart-selection.component';
import { AuftragRubrikSelectionComponent } from '../../../shared/widgets/auftrag-rubrik-selection/auftrag-rubrik-selection.component';
import { AuftragDatumSelectionComponent } from '../../../shared/widgets/auftrag-datum-selection/auftrag-datum-selection.component';
import { AuftragRechteSelectionComponent } from '../../../shared/widgets/auftrag-rechte-selection/auftrag-rechte-selection.component';
import { AuftragGrundstueckSelectionComponent } from '../../../shared/widgets/auftrag-grundstuecktype-selection/auftrag-grundstuecktype-selection.component';
import { AuftragBodenbedeckungSelectionComponent } from '../../../shared/widgets/auftrag-bodenbedeckung-selection/auftrag-bodenbedeckung-selection.component';
import { CdkStepperPrevious } from '@angular/cdk/stepper';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'cap-select-auftragsbestand',
  templateUrl: './select-auftragsbestand.component.html',
  styleUrls: ['./select-auftragsbestand.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    AuftragExportSelectionComponent,
    MetaDisablerDirective,
    MetaValidatorDirective,
    AuftragPersonartSelectionComponent,
    AuftragRubrikSelectionComponent,
    AuftragDatumSelectionComponent,
    AuftragRechteSelectionComponent,
    AuftragGrundstueckSelectionComponent,
    AuftragBodenbedeckungSelectionComponent,
    CdkStepperPrevious,
    RouterLink,
    TranslatePipe
  ]
})
export class SelectAuftragsbestandComponent implements OnInit, ControlValueAccessor {
  @ViewChild('form', { static: true })
  form: NgForm;

  @Input()
  disabled: boolean;

  @Input()
  readonly: boolean;

  @Input()
  wizard: CapiWizardComponent;

  _template: SelektionskriterienDetailEntryMeta;
  model: SelektionskriterienDetailEntryMeta;

  propagateChange: (id: string) => void;
  propagateTouch: (_) => void;

  constructor(
    private capAlertService: CapAlertService,
    private translate: TranslateService
  ) {
    keysToTranslate<string>([
      'EingabeMussFolgendesFeldAngegebenHaben',
      'ZeitraumVon',
      'EingabeMussMindestensEineBodenbedeckungAusgewaehltHaben',
      'EingabeMussGenauEineGemeindeAusgewaehltHaben',
      'GrundstueckMitAenderungen',
      'GeloeschteGrundstuecke',
      'DatumDesExports'
    ]);
  }

  @Input()
  set template(template: SelektionskriterienDetailEntryMeta) {
    if (!template) {
      return;
    }
    this.applyNewTemplate();
    this._template = template;
  }

  ngOnInit() {}

  public validateAndGoToNextStep() {
    if (
      this._template.exportTyp === 'GRUNDSTUECKLISTE' &&
      (!this._template.fileSelection || !this._template.fileSelection.fileName || !this._template.fileSelection.fileContent)
    ) {
      this.capAlertService.warning(this.translate.instant('GueltigeDateiAuswaehlen'), CapAlertLevel.CONTENT_HEADER);
      return;
    }
    if (this._template.exportTyp !== 'GRUNDSTUECKLISTE' && this._template.gemeindeId.length !== 1) {
      this.addValidierungsAlertWithParamKey('EingabeMussGenauEineGemeindeAusgewaehltHaben');
      return;
    }
    if (this.disabled) {
      this.wizard.next();
      return;
    }
    if (
      (this._template.loeschungsDatumSelection && !this._template.loeschungsDatumSelection.datumVon) ||
      (this._template.aenderungsDatumSelection && !this._template.aenderungsDatumSelection.datumVon)
    ) {
      const params = {
        fehlerMeldung: this.translate.instant('EingabeMussFolgendesFeldAngegebenHaben', { Feld: this.translate.instant('ZeitraumVon') })
      };
      this.addValidierungsAlertWithParams(params);
    } else if (this._template.bodenbedeckungSelection && this._template.bodenbedeckungSelection.bodenbedeckungCodes.length === 0) {
      this.addValidierungsAlertWithParamKey('EingabeMussMindestensEineBodenbedeckungAusgewaehltHaben');
    } else if (this.form.valid) {
      this.wizard.next();
    } else {
      Object.values(this.form.controls).forEach((control, index) => {
        if (control.invalid) {
          setTimeout(() => {
            this.addValidierungsAlertWithParamKey(Object.keys(this.form.controls)[index]);
          }, 50);
        }
      });
    }
  }

  private addValidierungsAlertWithParamKey(key: string) {
    const params = { fehlerMeldung: this.translate.instant(key) };
    this.addValidierungsAlertWithParams(params);
  }

  private addValidierungsAlertWithParams(params: { fehlerMeldung: string }) {
    this.capAlertService.warning(this.translate.instant('ValidierungsFehler', params), CapAlertLevel.CONTENT_HEADER);
  }

  private applyNewTemplate() {
    this.form.reset();
    if (!this.disabled) {
      this.form.form.enable();
    } else {
      this.form.form.disable();
    }
  }

  writeValue(obj: any): void {
    this.model = obj;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }
}
